/* ===========================================================================
   PF MOTION SYSTEM — behaviour

   The companion to assets/pf-motion.css. Everything here is lifted from the
   Round 7 logo-motion catalog (PF-Motion-Playground.html, catalog v206); each
   function names the cut it implements so the two can be checked against each
   other.

     reveal ........ 8a  cards enter on the standard rise
     settle ........ 8b  grid crossfades while a filter changes what it shows
     roll .......... 10f the cart badge rolls the number directionally
     step .......... 10f a quantity stepper rolls its own value the way pressed
     flyToBag ...... 12e a monogram coin arcs from the button to the bag
     leafFall ...... 19i six leaves shake loose from a pressed add-to-bag
     constellation . 19b the traced mark scatters on hover, stirs on click
     moth .......... 24e a moth trails the pointer on slower wings
     logo .......... 2c / 2d the header lockup wakes, then rises as sap

   Ambient work (moth, constellation) is pointer-fine only and never runs under
   prefers-reduced-motion. Nothing here is required for the page to function:
   every cut is decoration over markup that already works without it.
   ========================================================================= */
(function () {
  'use strict';

  if (window.PFMotion) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(pointer: fine)');

  function isReduced() { return reduced.matches; }

  /* The catalog's speed dial. Production ships at 1; leaving the read here
     means a designer can slow the whole system from one custom property
     without touching a duration. */
  function sp() {
    var v = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--pf-sp')
    );
    return isFinite(v) && v > 0 ? v : 1;
  }

  /* The brand marks, resolved by Liquid in layout/theme.liquid so the CDN's
     versioned URLs are used rather than a guessed path. */
  function assetUrl(name) {
    var map = window.PFMotionAssets;
    return (map && map[name]) || null;
  }

  /* -------------------------------------------------------------------------
     CUT 8a — PLP arrival.
     One observer for the whole page. Cards are indexed within their own
     container so the stagger reads as reading order, and the index is capped
     at five steps: past that the wait stops feeling like choreography and
     starts feeling like lag.
     ---------------------------------------------------------------------- */
  var revealObserver = null;
  var pending = [];          // observed, not yet played
  var sweepQueued = false;

  function play(el) {
    if (!el || el.classList.contains('pf-play')) return;
    el.classList.add('pf-play');
    var i = pending.indexOf(el);
    if (i !== -1) pending.splice(i, 1);
    if (revealObserver) revealObserver.unobserve(el);
    guard(el);
    // A stage reveals its own parts (the 404's line, body and way back), and
    // those parts are just as starvable as the cards are.
    el.querySelectorAll('.pf-guarded').forEach(guard);
  }

  /* The guarantee.

     A transition, like an animation, only advances when the page produces a
     rendering frame, and it holds its interpolated value while it waits. So on
     a page whose frame loop is starved, a card that has been told to appear is
     still sitting at opacity 0 — the class is right, the CSS is right, and the
     product is invisible anyway.

     Long after the rise should have finished, this asks the element what it
     actually looks like. If it is still hidden, the transition is cancelled and
     the final state written directly, which no starvation can hold back.
     Nothing to see when things are working; a product on the shelf when they
     are not. */
  function guard(el) {
    var wait = 800 * sp() + (parseFloat(el.style.getPropertyValue('--pf-i')) || 0) * 120 * sp() + 700;
    setTimeout(function () {
      if (!el.isConnected) return;
      if (parseFloat(getComputedStyle(el).opacity) > 0.9) return;
      // Both have to go. A transition holds its interpolated value until a
      // frame arrives, and an animation with a fill mode outranks an inline
      // style outright — so writing the final state is not enough on its own,
      // the thing still holding the property has to be taken off first.
      el.style.transition = 'none';
      el.style.animation = 'none';
      el.style.opacity = '1';
      el.style.transform = 'none';
    }, wait);
  }

  /* The geometric check, and the reason this does not rely on
     IntersectionObserver alone.

     On the desktop homepage the observer never delivers a callback at all —
     not even a fresh one, on a card measured at 95% inside the viewport — so
     every card sat at opacity 0 for ever and the homepage looked broken rather
     than animated. Whatever that page does to defeat the observer, a rect
     comparison cannot be defeated: it is the same question asked directly.

     The observer stays because it is the cheap path that handles the common
     case; this is what makes the result correct. */
  function sweep() {
    sweepQueued = false;
    if (!pending.length) return;
    var h = window.innerHeight || document.documentElement.clientHeight;
    for (var i = pending.length - 1; i >= 0; i--) {
      var el = pending[i];
      if (!el.isConnected) { pending.splice(i, 1); continue; }
      var r = el.getBoundingClientRect();
      if (r.height === 0 && r.width === 0) continue;
      // Anything whose top edge has entered the viewport, with a little margin
      // so a card is already moving by the time it is properly on screen.
      if (r.top < h * 0.94 && r.bottom > 0) play(el);
    }
  }

  function queueSweep() {
    if (sweepQueued) return;
    sweepQueued = true;
    requestAnimationFrame(sweep);
  }

  function ensureRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) play(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -6% 0px' });
    return revealObserver;
  }

  var sweepBound = false;
  function bindSweep() {
    if (sweepBound) return;
    sweepBound = true;
    // Scroll and resize coalesce through a frame, which is what they are for.
    addEventListener('scroll', queueSweep, { passive: true });
    addEventListener('resize', queueSweep, { passive: true });
    // The opening passes call sweep directly rather than through rAF. On the
    // desktop homepage no frame callback ever arrives — the same starvation
    // that keeps IntersectionObserver silent — so anything queued behind a
    // frame is queued behind something that never comes. Layout also keeps
    // moving as fonts and images land, so the pass is repeated rather than
    // trusted once.
    [0, 250, 700, 1500, 3000].forEach(function (t) { setTimeout(sweep, t); });
    addEventListener('load', sweep);
  }

  function observe(el) {
    if (isReduced() || !('IntersectionObserver' in window)) { el.classList.add('pf-play'); return; }
    pending.push(el);
    var io = ensureRevealObserver();
    if (io) io.observe(el);
    bindSweep();
  }

  function reveal(root) {
    var scope = root || document;
    var groups = new Map();

    scope.querySelectorAll('.pf-rise-in:not([data-pf-observed])').forEach(function (el) {
      el.setAttribute('data-pf-observed', '1');
      var parent = el.parentElement || document.body;
      var i = groups.get(parent) || 0;
      groups.set(parent, i + 1);
      el.style.setProperty('--pf-i', Math.min(i, 5));
      // Already on screen: it has been painted since first paint, so it stays.
      // Only what is still below the fold is hidden, to rise in on scroll.
      var r = el.getBoundingClientRect();
      if ((r.width || r.height) && r.top < innerHeight && r.bottom > 0) { el.classList.add('pf-play'); return; }
      el.setAttribute('data-pf-hide', '');
      observe(el);
    });

    // Standalone stages (the 404 scene) play as a whole rather than per item.
    scope.querySelectorAll('[data-pf-stage]:not([data-pf-observed])').forEach(function (el) {
      el.setAttribute('data-pf-observed', '1');
      observe(el);
    });
  }

  /* -------------------------------------------------------------------------
     CUT 8b — Filter settle.
     The caller owns the actual filtering; this owns the two beats around it.
     The old set exhales, `apply` runs while nothing is visible, the new set
     rises in. Height is never animated, so the page below never jumps.
     ---------------------------------------------------------------------- */
  /* The two halves of the settle, for a caller whose new content arrives on its
     own schedule — the PLP category swap fetches a page before it has anything
     to put back. Exhale on the click, breathe in when the content lands. */
  function settleOut(el) {
    if (!el || isReduced()) return;
    el.classList.add('pf-fg');
    if (el.pfSettleTimer) { clearTimeout(el.pfSettleTimer); el.pfSettleTimer = null; }
    el.classList.add('pf-settling');
  }

  function settleIn(el) {
    if (!el) return;
    el.classList.add('pf-fg');
    // Read the new layout back before releasing, so the rise starts from where
    // the content actually landed rather than animating through the reflow.
    void el.offsetHeight;
    el.classList.remove('pf-settling');
  }

  function settle(el, apply) {
    if (!el) { if (apply) apply(); return; }
    if (isReduced()) { if (apply) apply(); return; }

    var out = 260 * sp();
    el.classList.add('pf-fg');

    if (el.pfSettleTimer) clearTimeout(el.pfSettleTimer);
    el.classList.add('pf-settling');

    el.pfSettleTimer = setTimeout(function () {
      if (apply) apply();
      // Read back the new layout before releasing, so the rise starts from the
      // settled position rather than animating through the reflow.
      void el.offsetHeight;
      el.classList.remove('pf-settling');
      el.pfSettleTimer = null;
    }, out);
  }

  /* -------------------------------------------------------------------------
     CUT 10f — Count roll.
     Numbers rise from below when the count goes up and settle from above when
     it goes down, through a mask the size of the digit. The element keeps its
     own last value so any writer — the header, the cart preview, a stepper —
     gets the direction right without having to say which way it went.

     While a coin is in flight (12e) the roll is held back so the number ticks
     as the coin lands, not before it leaves.
     ---------------------------------------------------------------------- */
  var flightLandsAt = 0;

  function roll(el, value) {
    if (!el) return;
    var next = String(value);
    var prev = el.getAttribute('data-pf-count-prev');
    if (prev === null) prev = (el.textContent || '').trim();

    if (isReduced()) {
      el.textContent = next;
      el.setAttribute('data-pf-count-prev', next);
      return;
    }

    var wait = Math.max(0, flightLandsAt - Date.now());
    if (wait > 0) {
      if (el.pfRollTimer) clearTimeout(el.pfRollTimer);
      el.pfRollTimer = setTimeout(function () {
        el.pfRollTimer = null;
        roll(el, next);
      }, wait);
      return;
    }

    el.setAttribute('data-pf-count-prev', next);
    if (prev === next) { paintCount(el, next, null); return; }

    var up = (parseFloat(next) || 0) >= (parseFloat(prev) || 0);
    paintCount(el, next, up ? 'pf-roll-up' : 'pf-roll-down');
  }

  /* The steppers. Same cut, but the value element is the one that moves: an
     <input> cannot carry a child span, and the cart rows show their number in
     an <output>. The mask is whatever the number already sits inside — the
     stepper pill — so no template has to grow a wrapper for it.

     Direction comes from the caller rather than from the value, because a
     stepper always knows which button was pressed, and a clamped press (minus
     at 1) should still answer with a downward roll. */
  function step(el, direction) {
    if (!el || isReduced()) return;
    var mask = el.parentNode;
    if (mask && mask.nodeType === 1) mask.classList.add('pf-count-mask');
    // Transforms do not apply to an inline box, and an <output> or <span> is
    // inline until something says otherwise. Promoted here rather than in the
    // stylesheet so a value the page is deliberately hiding stays hidden.
    if (getComputedStyle(el).display === 'inline') el.style.display = 'inline-block';
    el.classList.add('pf-stepnum');
    el.classList.remove('pf-roll-up', 'pf-roll-down');
    // Restart the animation on a repeat of the same direction.
    void el.offsetWidth;
    el.classList.add(direction >= 0 ? 'pf-roll-up' : 'pf-roll-down');
  }

  function paintCount(el, text, rollClass) {
    el.classList.add('pf-count');
    var digit = el.querySelector('.pf-count__n');
    if (!digit) {
      el.textContent = '';
      digit = document.createElement('span');
      digit.className = 'pf-count__n';
      el.appendChild(digit);
    }
    digit.classList.remove('pf-roll-up', 'pf-roll-down');
    // Restart the animation on a repeat of the same direction.
    void digit.offsetWidth;
    digit.textContent = text;
    if (rollClass) digit.classList.add(rollClass);
  }

  /* -------------------------------------------------------------------------
     CUT 12e — The coin takes flight.
     A monogram coin lifts off the button, arcs to the bag, and the bag takes
     one breath as the count ticks. The arc is built per press from the two
     rectangles, so it works from any button on any page rather than from one
     hard-coded path.
     ---------------------------------------------------------------------- */
  /* Where the coin is going. Normally the bag itself — but the header is only
     fixed on some templates, so on a long page the cart icon is very often
     scrolled out of the window by the time the shopper reaches an add button.
     This used to return null there and flyToBag gave up, so the press on the
     desktop PDP answered with nothing at all. When the bag is off screen the
     coin is aimed at the top edge under where the bag sits instead: it still
     leaves toward the cart, which is the whole point of the cut.

     `el` is the bag element when there is one on screen to take the breath at
     the end of the flight, and null when the coin is headed off the top. */
  function bagPoint() {
    var targets = document.querySelectorAll(
      '[data-header-cart-link],[data-pf-bag],.desktop-hero__icon[aria-label="Cart"]'
    );
    var offscreen = null;
    for (var i = 0; i < targets.length; i++) {
      var r = targets[i].getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (r.top < window.innerHeight && r.bottom > 0) {
        return { x: r.left + r.width / 2, y: r.top + r.height / 2, el: targets[i] };
      }
      if (!offscreen) offscreen = r;
    }
    if (!offscreen) return null;
    var x = offscreen.left + offscreen.width / 2;
    return { x: Math.min(Math.max(x, 24), window.innerWidth - 24), y: 14, el: null };
  }

  function flyToBag(from) {
    if (isReduced() || !from) return;
    var bag = bagPoint();
    if (!bag) return;

    var a = from.getBoundingClientRect();
    if (!a.width || !a.height) return;
    var x0 = a.left + a.width / 2;
    var y0 = a.top + a.height / 2;
    var x1 = bag.x;
    var y1 = bag.y;

    // One high control arc: the coin leaves upward, travels, and drops onto
    // the bag. Lifting both handles keeps it a rise rather than a straight
    // throw, which is the difference between "drifts" and "flies at you".
    //
    // The lift is then clamped to the room above the lower of the two points.
    // Unclamped, a button low on a long page threw the coin clean over the top
    // of the window and the whole flight happened off screen — the cut ran
    // perfectly and nobody could see it.
    var lift = Math.max(70, Math.hypot(x1 - x0, y1 - y0) * 0.42);
    var headroom = Math.min(y0, y1) - 16;
    if (lift > headroom) lift = Math.max(24, headroom);
    var path =
      'M ' + x0.toFixed(1) + ' ' + y0.toFixed(1) +
      ' C ' + (x0 + (x1 - x0) * 0.2).toFixed(1) + ' ' + (y0 - lift).toFixed(1) +
      ', ' + (x0 + (x1 - x0) * 0.7).toFixed(1) + ' ' + (y1 - lift * 0.8).toFixed(1) +
      ', ' + x1.toFixed(1) + ' ' + y1.toFixed(1);

    /* The badge will not tick until this lands (see roll()), so the flight
       time is also the cart count's latency. At 950ms the number sat stale
       for most of a second after a press and read as broken; 560ms keeps the
       arc legible while the count follows the click closely. */
    var duration = 560 * sp();
    flightLandsAt = Date.now() + duration * 0.88;

    /* The coin launches on the click, never on a network response — its face
       is a CSS mask (see .pf-flyer__coin in pf-motion.css), so there is nothing
       left to wait for. It used to be fetched and inlined, which meant the
       first add of a visit paid a round-trip before the flight started, and on
       the dev server — where the asset is served from another origin over
       http — the fetch failed outright and every coin flew blank. */
    var flyer = document.createElement('div');
    flyer.className = 'pf-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.offsetPath = 'path("' + path + '")';
    // Safari still ships the prefixed property.
    flyer.style.webkitOffsetPath = 'path("' + path + '")';
    flyer.style.animationDuration = duration + 'ms';

    var coin = document.createElement('span');
    coin.className = 'pf-flyer__coin';
    flyer.appendChild(coin);
    document.body.appendChild(flyer);

    setTimeout(function () {
      flyer.remove();
      // Nothing to breathe when the coin went off the top of the window.
      if (!bag.el) return;
      var icon = bag.el.querySelector('svg, img') || bag.el;
      icon.classList.remove('pf-bag-breath');
      void icon.offsetWidth;
      icon.classList.add('pf-bag-breath');
      setTimeout(function () { icon.classList.remove('pf-bag-breath'); }, 700 * sp());
    }, duration);
  }

  /* -------------------------------------------------------------------------
     CUT 19i — Leaf fall.
     Six leaves shake loose from the pressed button and drift to the floor.
     The offsets are the catalog's, so the burst keeps its asymmetry rather
     than reading as a symmetrical puff. One burst per press.
     ---------------------------------------------------------------------- */
  var LEAVES = [
    { l: 0.14, w: 11, h: 7, r: '65% 35% 60% 40%', c: '#6A8F59', fx: -44, fr: -170, fd: 0 },
    { l: 0.34, w: 9, h: 6, r: '60% 40% 55% 45%', c: '#88B14B', fx: -18, fr: 140, fd: 0.12 },
    { l: 0.52, w: 12, h: 7, r: '55% 45% 65% 35%', c: '#315E56', fx: 26, fr: -120, fd: 0.05 },
    { l: 0.68, w: 8, h: 5, r: '60% 40% 50% 50%', c: '#6A8F59', fx: 40, fr: 190, fd: 0.2 },
    { l: 0.84, w: 10, h: 6, r: '65% 35% 55% 45%', c: '#88B14B', fx: 52, fr: 150, fd: 0.3 },
    { l: 0.24, w: 8, h: 5, r: '55% 45% 60% 40%', c: '#315E56', fx: -58, fr: 200, fd: 0.4 }
  ];

  function leafFall(button) {
    if (isReduced() || !button) return;
    var r = button.getBoundingClientRect();
    if (!r.width) return;

    var speed = sp();
    LEAVES.forEach(function (leaf) {
      var el = document.createElement('span');
      el.className = 'pf-leaf';
      el.setAttribute('aria-hidden', 'true');
      el.style.left = (r.left + r.width * leaf.l) + 'px';
      el.style.top = (r.top + r.height * 0.62) + 'px';
      el.style.width = leaf.w + 'px';
      el.style.height = leaf.h + 'px';
      el.style.borderRadius = leaf.r;
      el.style.background = leaf.c;
      el.style.setProperty('--pf-fx', leaf.fx + 'px');
      el.style.setProperty('--pf-fr', leaf.fr + 'deg');
      el.style.setProperty('--pf-fd', (leaf.fd * speed) + 's');
      document.body.appendChild(el);
      setTimeout(function () { el.remove(); }, (1800 + leaf.fd * 1000) * speed + 120);
    });
  }

  /* Press ring — the button settles and one ink ring leaves its rim. Added
     lazily so no template has to carry an extra span for it. */
  function ensurePressRing(button) {
    if (!button || button.querySelector('.pf-press__ring')) return;
    button.classList.add('pf-press');
    var ring = document.createElement('span');
    ring.className = 'pf-press__ring';
    ring.setAttribute('aria-hidden', 'true');
    button.appendChild(ring);
  }

  /* -------------------------------------------------------------------------
     CUT 19b — The constellation.
     The emblem's inhabitants are separable subpaths of one traced master path.
     They are lifted out of the mass into their own groups so each can drift on
     its own; the mass keeps every subpath that is not a creature, so the
     drawing stays whole while they are home.

     The trace is a quarter-megabyte of path data, so it is fetched lazily and
     only where a host asks for it — never on a phone, never under reduced
     motion, and never before the visitor shows intent.
     ---------------------------------------------------------------------- */
  var CARVE = {
    df: [306],
    wing: [48],
    swan: [130, 137, 150, 153, 157, 159, 160, 162, 165],
    rip1: [175],
    rip2: [182],
    cnp: [19, 21, 27, 30, 36, 37, 38, 44, 46, 52, 57, 66, 67]
  };

  function stripIds(markup) {
    return markup.replace(/\sid="[^"]*"/g, '');
  }

  function innerOf(svgText) {
    return svgText.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
  }

  function carve(inner) {
    return inner.replace(/<path\b[^>]*id="pf-master"[^>]*>/, function (tag) {
      var dMatch = tag.match(/ d="([^"]+)"/);
      if (!dMatch) return tag;
      var subs = dMatch[1].split(/(?=M)/);
      var tfmMatch = tag.match(/transform="([^"]+)"/);
      var tfm = tfmMatch ? tfmMatch[1] : '';

      var cut = {};
      Object.keys(CARVE).forEach(function (key) {
        CARVE[key].forEach(function (i) { cut[i] = true; });
      });

      var mass = subs.filter(function (_, i) { return !cut[i]; }).join('');
      var groups = Object.keys(CARVE).map(function (key) {
        var d = CARVE[key].map(function (i) { return subs[i]; }).join('');
        return '<g class="pfg-' + key + '"><path fill="currentColor" d="' + d + '"/></g>';
      }).join('');

      return (
        '<path transform="' + tfm + '" fill="currentColor" d="' + mass + '"/>' +
        '<g transform="' + tfm + '">' + groups + '</g>'
      );
    });
  }

  var emblemPending = null;

  function loadEmblem() {
    if (emblemPending) return emblemPending;
    var url = assetUrl('emblem');
    if (!url) return Promise.resolve(null);
    emblemPending = fetch(url)
      .then(function (r) { return r.ok ? r.text() : null; })
      .catch(function () { return null; });
    return emblemPending;
  }

  function mountConstellation(host) {
    if (!host || host.dataset.pfMounted) return;
    host.dataset.pfMounted = 'pending';

    loadEmblem().then(function (text) {
      if (!text) { delete host.dataset.pfMounted; return; }
      var target = host.querySelector('[data-pf-constellation-slot]') || host;
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'pf-constellation__svg');
      svg.setAttribute('viewBox', (text.match(/viewBox="([^"]+)"/) || [, '988 474 1532 1532'])[1]);
      svg.setAttribute('aria-hidden', 'true');
      svg.innerHTML = stripIds(carve(innerOf(text)));
      target.appendChild(svg);
      host.dataset.pfMounted = '1';
      host.classList.add('pf-mounted');
    });
  }

  function initConstellations() {
    if (isReduced() || !fine.matches) return;
    document.querySelectorAll('[data-pf-constellation]').forEach(function (host) {
      // Hover only, and never on idle. The trace is a quarter-megabyte of path
      // data for an easter egg, so the cost is paid by the visitor who goes
      // looking for it and by nobody else.
      host.addEventListener('pointerenter', function () {
        mountConstellation(host);
      }, { once: true });

      host.addEventListener('click', function () {
        if (!host.dataset.pfMounted) return;
        host.classList.add('pf-chorus');
        clearTimeout(host.pfChorusTimer);
        host.pfChorusTimer = setTimeout(function () {
          host.classList.remove('pf-chorus');
        }, 2400 * sp());
      });
    });
  }

  /* -------------------------------------------------------------------------
     CUT 24e — The chase.
     The moth trails the pointer on slower wings, never quite catching up.
     Lerp 0.05, one rAF that stops as soon as it has arrived, pointer-fine
     devices only. The catalog's version hides the cursor and makes it a
     lantern; on a footer full of links the cursor stays where it is and only
     the moth follows.
     ---------------------------------------------------------------------- */
  function initMoths() {
    if (isReduced() || !fine.matches) return;

    var hosts = [];

    document.querySelectorAll('[data-pf-moth]').forEach(function (host) {
      if (host.dataset.pfMothBound) return;
      host.dataset.pfMothBound = '1';
      host.classList.add('pf-moth-host');

      var moth = document.createElement('div');
      moth.className = 'pf-moth';
      moth.setAttribute('aria-hidden', 'true');
      moth.innerHTML = '<span class="pf-moth__wing"></span><span class="pf-moth__wing"></span>';
      host.appendChild(moth);

      hosts.push({ el: host, moth: moth, rect: null, x: null, y: null, tx: 0, ty: 0, raf: null });
    });

    if (!hosts.length) return;

    /* The rects are cached rather than measured per move. getBoundingClientRect
       on every pointermove forces a layout on every mouse move, for every host
       on the page — which on a footer full of links is a steady tax on
       scrolling and hovering for an ambient decoration nobody asked to pay for.
       Scroll and resize are the only things that can move a host, so those are
       what invalidate it. */
    function measure() {
      for (var i = 0; i < hosts.length; i++) hosts[i].rect = hosts[i].el.getBoundingClientRect();
    }

    /* Re-read on the first pointer move after a scroll or resize rather than in
       a frame callback: the desktop homepage can starve requestAnimationFrame,
       and a rect waiting on a frame that never comes left the moth asleep there
       for good. */
    var stale = false;
    function invalidate() { stale = true; }

    measure();
    addEventListener('scroll', invalidate, { passive: true });
    addEventListener('resize', invalidate, { passive: true });

    function step(h) {
      return function tick() {
        h.rafAt = 0;
        h.x += (h.tx - h.x) * 0.05;
        h.y += (h.ty - h.y) * 0.05;
        h.moth.style.transform =
          'translate(' + (h.x - 6).toFixed(1) + 'px,' + (h.y - 4).toFixed(1) + 'px)';
        h.raf = Math.abs(h.tx - h.x) + Math.abs(h.ty - h.y) > 0.5
          ? requestAnimationFrame(tick)
          : null;
      };
    }

    /* One listener for every host, not one each. The About Us hero sets
       pointer-events:none so its scroll pinning works, and a footer is a field
       of links that would swallow the pointer on their own way; from the
       document the moth follows the cursor across every child either way. */
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (stale) { stale = false; measure(); }
      for (var i = 0; i < hosts.length; i++) {
        var h = hosts[i];
        var r = h.rect;
        if (!r) continue;
        var inside =
          e.clientX >= r.left && e.clientX <= r.right &&
          e.clientY >= r.top && e.clientY <= r.bottom;

        if (!inside) { h.moth.classList.remove('pf-awake'); continue; }

        h.tx = e.clientX - r.left;
        h.ty = e.clientY - r.top;
        // Waking lands it at the cursor at once, so it shows even before the
        // first frame of the chase arrives.
        if (h.x === null || !h.moth.classList.contains('pf-awake')) {
          h.x = h.tx; h.y = h.ty;
          h.moth.style.transform = 'translate(' + (h.x - 6).toFixed(1) + 'px,' + (h.y - 4).toFixed(1) + 'px)';
        }
        h.moth.classList.add('pf-awake');
        if (!h.raf) { h.raf = requestAnimationFrame(step(h)); h.rafAt = performance.now(); }
        // No frame for 100ms means the page is starving them: keep up with the
        // cursor directly instead of waiting on a chase that cannot run.
        else if (h.rafAt && performance.now() - h.rafAt > 100) {
          h.x = h.tx; h.y = h.ty;
          h.moth.style.transform = 'translate(' + (h.x - 6).toFixed(1) + 'px,' + (h.y - 4).toFixed(1) + 'px)';
        }
      }
    }, { passive: true });
  }

  /* -------------------------------------------------------------------------
     CUTS 2c / 2d — the header lockup.
     First paint of a session gets the full wake: first light opens the mark,
     dawn blooms behind it, the lockup exhales and then breathes. Every later
     paint gets the sap rise instead — the same mark, a quieter arrival, and no
     ceremony repeated on the fourth page of a visit.
     ---------------------------------------------------------------------- */
  var WAKE_KEY = 'pf-logo-woken';
  var logoObserver = null;

  function isVisibleLogoPart(node) {
    if (!node) return false;
    var rect = node.getBoundingClientRect();
    var style = window.getComputedStyle(node);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  }

  function playLogo(logo) {
    if (!logo || logo.hasAttribute('data-pf-logo-played') || !isVisibleLogoPart(logo) || isReduced()) return;
    logo.setAttribute('data-pf-logo-played', '');

    var woken = false;
    try { woken = sessionStorage.getItem(WAKE_KEY) === '1'; } catch (e) {}

    if (!woken) {
      logo.classList.add('pf-logo-wake');
      var glow = document.createElement('span');
      glow.className = 'pf-logo-glow';
      glow.setAttribute('aria-hidden', 'true');
      logo.insertBefore(glow, logo.firstChild);
      try { sessionStorage.setItem(WAKE_KEY, '1'); } catch (e) {}
    } else {
      logo.classList.add('pf-logo-sap');
      var candidates = logo.querySelectorAll('.header__logo-image--base, .pf-logo-mark');
      var base = null;
      for (var i = 0; i < candidates.length; i++) {
        if (isVisibleLogoPart(candidates[i])) {
          base = candidates[i];
          break;
        }
      }
      if (!base && candidates.length) base = candidates[0];
      if (base && base.tagName === 'IMG') {
        var ghost = base.cloneNode(false);
        ghost.className = 'pf-logo-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.removeAttribute('alt');
        ghost.alt = '';
        logo.insertBefore(ghost, logo.firstChild);
      }
    }

    requestAnimationFrame(function () { logo.classList.add('pf-play'); });
    if (logoObserver) logoObserver.unobserve(logo);
  }

  function initLogo() {
    var logos = document.querySelectorAll('[data-pf-logo]:not([data-pf-logo-bound])');
    if (!logos.length || isReduced()) return;

    if ('IntersectionObserver' in window) {
      if (!logoObserver) {
        logoObserver = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) playLogo(entries[i].target);
          }
        }, { threshold: 0.05 });
      }

      for (var i = 0; i < logos.length; i++) {
        logos[i].setAttribute('data-pf-logo-bound', '');
        logoObserver.observe(logos[i]);
      }
      return;
    }

    for (var j = 0; j < logos.length; j++) {
      logos[j].setAttribute('data-pf-logo-bound', '');
      playLogo(logos[j]);
    }
  }

  /* -------------------------------------------------------------------------
     Wiring.
     ---------------------------------------------------------------------- */

  /* Every add-to-cart control in the theme, named once. These are the existing
     hooks the sections already carry — nothing had to be added to the markup
     for the motion to find them. */
  var ATC = [
    '[data-pf-atc]',
    '[data-pf-add-variant]',
    '[data-spotlight-panel-cta]',
    '[data-plp-quick-add]',
    '[data-plp-card-atc]',
    '[data-dpdp-rec-atc]',
    '[data-pdpv1-atc]',
    '[data-dpdp-atb]',
    '[data-dpdp-sticky-add]',
    '[data-atc]',
    '[data-pdpv1-submit][data-pdpv1-submit-mode="cart"]'
  ].join(',');

  /* -------------------------------------------------------------------------
     CUT 10g — "Chosen". A variant pill is a decision, so it answers like one:
     the chip settles under the press and a single ink ring leaves its rim.
     Wired to the size pills the PLP cards already carry, so no template had to
     grow an extra element for it.
     ---------------------------------------------------------------------- */
  var CHIPS = '[data-pf-chip],[data-plp-size-pill]';

  function initChips() {
    document.addEventListener('click', function (e) {
      var chip = e.target instanceof Element ? e.target.closest(CHIPS) : null;
      if (!chip || chip.disabled || isReduced()) return;

      chip.classList.add('pf-chip');
      var ring = chip.querySelector('.pf-chip__ring');
      if (!ring) {
        ring = document.createElement('span');
        ring.className = 'pf-chip__ring';
        ring.setAttribute('aria-hidden', 'true');
        chip.appendChild(ring);
      }
      chip.classList.remove('pf-chosen');
      void chip.offsetWidth;
      chip.classList.add('pf-chosen');
    }, true);
  }

  function initCartMoments() {
    document.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target.closest(ATC) : null;
      if (!target || target.disabled) return;
      ensurePressRing(target);
      leafFall(target);
      flyToBag(target);
    }, true);
  }

  /* Cut 10h — one status note for product-card adds. It is created on the
     first add, never blocks the request, and keeps a later add from being
     overwritten by an earlier request finishing out of order. */
  var leafNoteElement = null;
  var leafNoteText = null;
  var leafNoteTimer = 0;
  var leafNoteSequence = 0;

  function leafNote(title) {
    if (!document.body) return null;
    var created = false;
    if (!leafNoteElement) {
      created = true;
      leafNoteElement = document.createElement('div');
      leafNoteElement.className = 'pf-leaf-note';
      leafNoteElement.setAttribute('role', 'status');
      leafNoteElement.setAttribute('aria-live', 'polite');
      var mark = document.createElement('span');
      mark.className = 'pf-leaf-note__mark';
      mark.setAttribute('aria-hidden', 'true');
      leafNoteText = document.createElement('span');
      leafNoteText.className = 'pf-leaf-note__text';
      leafNoteElement.appendChild(mark);
      leafNoteElement.appendChild(leafNoteText);
      document.body.appendChild(leafNoteElement);
    }

    var sequence = ++leafNoteSequence;
    window.clearTimeout(leafNoteTimer);
    leafNoteElement.classList.remove('is-error');
    leafNoteText.textContent = title ? 'Adding ' + title + '…' : 'Adding to bag…';
    if (created) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (sequence === leafNoteSequence) leafNoteElement.classList.add('is-visible');
        });
      });
    } else {
      leafNoteElement.classList.add('is-visible');
    }

    function finish(message, failed) {
      if (sequence !== leafNoteSequence) return;
      leafNoteElement.classList.toggle('is-error', failed);
      leafNoteText.textContent = message;
      leafNoteTimer = window.setTimeout(function () {
        if (sequence === leafNoteSequence) leafNoteElement.classList.remove('is-visible');
      }, 2400);
    }

    return {
      added: function () { finish(title ? 'Added ' + title + ' to bag' : 'Added to bag', false); },
      failed: function () { finish('Could not add to bag', true); }
    };
  }

  function init() {
    reveal(document);
    initLogo();
    initCartMoments();
    initChips();
    initMoths();
    initConstellations();

    /* Sections that render after first paint — the theme editor, the PLP
       merging its later pages into the grid — still need their cards indexed.

       The scan is scoped to the nodes that were actually added, and skips
       entirely when a mutation added no elements. Re-querying the whole
       document on every mutation is what made the home page drag: the
       carousels mutate constantly, and each one was costing two
       document-wide querySelectorAll calls. */
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.classList.contains('pf-rise-in') || node.hasAttribute('data-pf-stage')) {
              reveal(node.parentNode || document);
            } else if (node.firstElementChild) {
              reveal(node);
            }
            if (node.hasAttribute('data-pf-logo') || node.querySelector('[data-pf-logo]')) initLogo();
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.PFMotion = {
    reveal: reveal,
    settle: settle,
    settleOut: settleOut,
    settleIn: settleIn,
    roll: roll,
    step: step,
    flyToBag: flyToBag,
    leafFall: leafFall,
    leafNote: leafNote,
    reduced: isReduced
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
